<template>
  <div class="card card--active">
    <TemporaryPopup ref="temporary-popup">
      Kopiert
    </TemporaryPopup>
    <div class="card__text">
      <p class="card__description" v-if="keyDescText">{{ keyDescText }}</p>
      <h3 class="card__title">{{ keyText }}</h3>
    </div>
    <button
      type="button"
      @click="copy()"
      class="button button--link api-key__copy"
    >
      <IconCopy />
    </button>
    <button
      type="button"
      class="button button--link icon-trash"
      v-if="!showAsDisabled"
      @click.stop="toggleActions()"
    >
      <IconTrash />
    </button>
    <div :class="actionsClasses" @click.stop="">
      <a :class="disconnectLinkClasses" @click="deleteKey">
        <IconTimes />
        API Key löschen
      </a>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, useTemplateRef } from 'vue'
  import IconTrash from '@/components/icons/Trash.vue'
  import IconTimes from '@/components/icons/Times.vue'
  import IconCopy from '@/components/icons/Copy.vue'
  import TemporaryPopup from '@/components/popups/TemporaryPopup.vue'
  import { useApiManagerStore } from '@/stores/useApiManagerStore'

  const props = defineProps({
    keyValue: { type: String },
    keyDesc: { type: String },
    showAsDisabled: { type: Boolean },
  })

  const apiManagerStore = useApiManagerStore()
  const keyText = ref(props.keyValue)
  const keyDescText = ref(props.keyDesc)
  const temporaryPopupRef = useTemplateRef('temporary-popup')

  const copy = () => {
    navigator.clipboard.writeText(keyText.value)
    temporaryPopupRef.value.showTemporarily()
  }
  const deleteKey = () => {
    apiManagerStore.deleteApiKey(keyText.value)
  }

  const showActions = ref(false)
  const actionsClasses = computed(() => {
    let result = ['producer__actions']
    if (showActions.value) {
      result.push('producer__actions--visible')
    }
    return result.join(' ')
  })
  const disconnectLinkClasses = computed(() => {
    let classes = ['producer__action-disconnect']
    return classes.join(' ')
  })
  let bodyOnClickHandler = false
  const hideActions = function () {
    showActions.value = false
    document.body.removeEventListener('click', bodyOnClickHandler)
  }
  const toggleActions = function () {
    showActions.value = !showActions.value
    if (showActions.value) {
      bodyOnClickHandler = hideActions
      document.body.addEventListener('click', bodyOnClickHandler)
    } else {
      document.body.removeEventListener('click', bodyOnClickHandler)
    }
  }
</script>

<style lang="scss" scoped>
  .api-key__copy {
    max-width: 3em;
    margin-left: 0.5rem;
  }

  .api-key__remove {
    font-weight: $font-weight-bold;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .api-key__remove::before {
    content: '';
    display: block;
    width: 0.6em;
    height: 0.12em;
    background: $error-color;
    border-radius: 2px;
    margin: 1em 0;
    transition: $transition-base;
  }

  .icon-trash {
    width: $spacer30;
    margin-left: $spacer05;
    cursor: pointer;
  }

  .card {
    height: 100%;
    display: flex;
    align-items: center;
    position: relative;
    padding: $spacer10;
    background-color: $grey-lightest;
  }

  .card__text {
    flex-grow: 1;
  }

  .card__icon {
    width: 2.4rem;
  }

  .card__description {
    margin-bottom: 0;
    color: $grey-darker;
    font-size: 0.9em;
  }

  .card__title {
    margin-bottom: 0;
    font-size: $font-size-base;
    line-height: $line-height-sm;

    @media (min-width: $bp-ssm) {
      font-size: $font-size-lg;
    }
  }

  .card__image {
    height: 2rem;
    margin-right: $spacer05;

    @media (min-width: $bp-ssm) {
      height: 3rem;
      margin-right: $spacer20;
    }
  }

  .card__button {
    position: static;
    display: flex;
    margin-left: $spacer05;
    width: $spacer20;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
    }
  }

  $producer-padding-top: $spacer10;

  .producer {
    display: flex;
    flex-wrap: wrap;
    padding: $producer-padding-top 0;
    position: relative;

    &--highlighted {
      background: $grey-lightest;
    }

    &__toggle-actions {
      color: $grey-light;
      cursor: pointer;
      transition: $transition-base;
      width: $spacer20;
      padding: 0 $spacer05;
      align-self: flex-end;
      position: absolute;
      top: $producer-padding-top + 1.15rem;
      right: 0;

      @include hover-focus-active {
        color: $community;
      }

      &--active {
        color: $community;
      }

      .icon {
        width: $spacer05;
      }
    }

    &__actions {
      position: absolute;
      right: 0;
      top: 100%;
      left: auto;
      overflow: hidden;
      transition: opacity 0.3s ease 0s, visibility 0s linear 0.5s;
      padding: $spacer05 $spacer10;
      border: 1px solid $grey-light;
      border-radius: $border-radius;
      opacity: 0;
      width: 200px;
      background: $white;
      visibility: hidden;
      z-index: 10;

      @media (min-width: $bp-sm) {
        right: $spacer05;
      }

      &--visible {
        visibility: visible;
        transition: visibility 0s linear 0s, opacity 0.3s ease 0.1s;
        opacity: 1;
        box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
      }

      &-overlay {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
      }
    }

    &__action-disconnect {
      cursor: pointer;
      transition: $transition-base;

      @include hover-focus-active {
        color: $error-color;
      }

      .icon {
        margin-right: $spacer025;
        width: 0.7rem;
        vertical-align: middle;
      }

      &--disconnecting {
        .icon {
          animation: spin 2s linear 0s infinite;
        }
      }
    }
  }
</style>
